/**
 * Fail / continue UI stubs — fair continue economy (GDD-SHARED).
 * Presentational only; RunLoop owns accept/decline.
 */

export interface ContinueHud {
  readonly root: HTMLElement;
  show(opts: {
    reason: "count_zero" | "mecha_dead" | "continue_offer" | string;
    continuesUsed: number;
    maxContinues?: number;
  }): void;
  hide(): void;
  dispose(): void;
}

export interface ContinueHudOptions {
  readonly parent?: HTMLElement;
  readonly onAccept?: () => void;
  readonly onDecline?: () => void;
}

/**
 * Modal stub for continue_offer / results_lose.
 * Buttons re-enable pointer events on the overlay only.
 */
export function createContinueHud(options: ContinueHudOptions = {}): ContinueHud {
  const root = document.createElement("div");
  root.className = "afr-continue-hud";
  root.setAttribute("data-testid", "continue-hud");
  root.setAttribute("hidden", "");
  Object.assign(root.style, {
    position: "absolute",
    inset: "0",
    display: "none",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(15, 23, 42, 0.72)",
    zIndex: "20",
    pointerEvents: "auto",
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    color: "#f8fafc",
  } satisfies Partial<CSSStyleDeclaration>);

  const card = document.createElement("div");
  Object.assign(card.style, {
    width: "min(88%, 320px)",
    padding: "24px 20px",
    borderRadius: "12px",
    background: "rgba(30, 41, 59, 0.95)",
    boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
    textAlign: "center",
  } satisfies Partial<CSSStyleDeclaration>);
  root.appendChild(card);

  const title = document.createElement("div");
  title.setAttribute("data-testid", "continue-title");
  Object.assign(title.style, {
    fontSize: "22px",
    fontWeight: "800",
    marginBottom: "8px",
  } satisfies Partial<CSSStyleDeclaration>);
  title.textContent = "Continue?";
  card.appendChild(title);

  const body = document.createElement("div");
  body.setAttribute("data-testid", "continue-body");
  Object.assign(body.style, {
    fontSize: "14px",
    opacity: "0.85",
    marginBottom: "18px",
    lineHeight: "1.4",
  } satisfies Partial<CSSStyleDeclaration>);
  body.textContent = "Restore and keep fighting.";
  card.appendChild(body);

  const row = document.createElement("div");
  Object.assign(row.style, {
    display: "flex",
    gap: "10px",
    justifyContent: "center",
  } satisfies Partial<CSSStyleDeclaration>);
  card.appendChild(row);

  const makeBtn = (label: string, testId: string, primary: boolean): HTMLButtonElement => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = label;
    btn.setAttribute("data-testid", testId);
    Object.assign(btn.style, {
      flex: "1",
      padding: "12px 10px",
      borderRadius: "8px",
      border: "none",
      fontWeight: "700",
      fontSize: "14px",
      cursor: "pointer",
      background: primary ? "#38bdf8" : "rgba(148,163,184,0.25)",
      color: primary ? "#0f172a" : "#f8fafc",
    } satisfies Partial<CSSStyleDeclaration>);
    return btn;
  };

  const acceptBtn = makeBtn("Continue", "continue-accept", true);
  const declineBtn = makeBtn("Give up", "continue-decline", false);
  row.appendChild(acceptBtn);
  row.appendChild(declineBtn);

  acceptBtn.addEventListener("click", () => options.onAccept?.());
  declineBtn.addEventListener("click", () => options.onDecline?.());

  const parent = options.parent ?? document.body;
  parent.appendChild(root);

  let mode: "offer" | "lose" = "offer";

  const show: ContinueHud["show"] = (opts) => {
    const isOffer =
      opts.reason === "continue_offer" ||
      opts.reason === "count_zero" ||
      opts.reason === "mecha_dead";
    // Caller passes phase; treat explicit lose
    mode = opts.reason === "results_lose" ? "lose" : "offer";
    if (mode === "lose" || !isOffer) {
      // Still allow show for lose with only decline
    }

    if (opts.reason === "results_lose") {
      title.textContent = "Defeat";
      body.textContent = "Squad wiped. Tap to return.";
      acceptBtn.style.display = "none";
      declineBtn.textContent = "OK";
      declineBtn.style.flex = "1";
    } else {
      title.textContent = "Continue?";
      const used = opts.continuesUsed;
      const max = opts.maxContinues ?? 1;
      body.textContent =
        opts.reason === "mecha_dead"
          ? `Mecha destroyed. Continues ${used}/${max} — restore 35% HP.`
          : `Squad down. Continues ${used}/${max} — restore starting force.`;
      acceptBtn.style.display = "block";
      declineBtn.textContent = "Give up";
    }

    root.style.display = "flex";
    root.removeAttribute("hidden");
  };

  const hide = (): void => {
    root.style.display = "none";
    root.setAttribute("hidden", "");
  };

  const dispose = (): void => {
    root.remove();
  };

  return { root, show, hide, dispose };
}
